"use client";

import { Form, Formik } from "formik";
import Link from "next/link";
import { FC } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, Input, Typography } from "@/shared/ui";

import styles from "./forgot-password-form.module.scss";
import { ForgotPasswordFormScheme } from "./forgot-password-form.types";
import { useRequestResetPassword } from "./hooks/request-reset-password.hook";

const validationScheme = object({
  email: string().email("Wrong email format").min(1, "Field is required"),
});

const ForgotPasswordForm: FC = () => {
  const { mutate, isPending } = useRequestResetPassword();

  const onSubmitHandler = (data: ForgotPasswordFormScheme) => {
    mutate(data);
  };

  return (
    <div className={styles.forgotPasswordForm}>
      <div className={styles.header}>
        <Typography bold variant="large-title">
          Forgot Password?
        </Typography>
        <Typography variant="caption-1">Enter your email to start password recovery</Typography>
      </div>

      <Formik<ForgotPasswordFormScheme>
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ email: "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, touched, isValid, setFieldValue, setFieldTouched, validateField }) => (
          <Form className={styles.form}>
            <Input
              required
              placeholder="Email"
              value={values.email}
              message={errors.email}
              variant={errors.email ? "error" : undefined}
              onBlur={() => {
                validateField("email");
                setFieldTouched("email");
              }}
              onValueChange={async (value) => {
                await setFieldValue("email", value);
                errors.email && (await validateField("email"));
              }}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={isPending || !touched.email || !isValid || !values.email}
            >
              {"Continue"}
            </Button>

            <Link href={"/auth/login"} className={styles.backButton}>
              <Typography as="span" variant="caption-1" align="center">
                Back to Log In
              </Typography>
            </Link>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export { ForgotPasswordForm };
